/**
 * Reusable toggle picker for telemetry data types.
 *
 * @module components/ui/TelemetryDataPicker
 */

import { CheckCircle, Circle } from 'lucide-react';
import type { TelemetryData } from '@/types';

const DATA_TYPE_LABELS: Record<TelemetryData, string> = {
  ContainerStdoutStderr: 'Stdout/Stderr',
  ContainerOtel: 'OTel Signals',
  Metrics: 'Metrics',
};

const ALL_DATA_TYPES: TelemetryData[] = ['ContainerStdoutStderr', 'ContainerOtel', 'Metrics'];

export interface TelemetryDataPickerProps {
  selected: TelemetryData[];
  onChange: (data: TelemetryData[]) => void;
  /** Restrict which data types are shown. Defaults to all types. */
  availableTypes?: TelemetryData[];
}

/** Toggle-pill picker for selecting which telemetry data types to route. */
export function TelemetryDataPicker({
  selected,
  onChange,
  availableTypes = ALL_DATA_TYPES,
}: TelemetryDataPickerProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Data Types
      </label>
      <div className="flex flex-wrap gap-2 mt-2">
        {availableTypes.map((dataType) => {
          const isSelected = selected.includes(dataType);
          return (
            <button
              key={dataType}
              type="button"
              onClick={() =>
                onChange(
                  isSelected ? selected.filter((d) => d !== dataType) : [...selected, dataType]
                )
              }
              className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md border transition-colors cursor-pointer ${
                isSelected
                  ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-400 dark:border-primary-600 text-primary-700 dark:text-primary-300 font-medium'
                  : 'bg-white dark:bg-[#161616] border-gray-300 dark:border-[#333] text-gray-500 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500'
              }`}
            >
              {isSelected ? (
                <CheckCircle className="h-3.5 w-3.5" />
              ) : (
                <Circle className="h-3.5 w-3.5" />
              )}
              {DATA_TYPE_LABELS[dataType]}
            </button>
          );
        })}
      </div>
    </div>
  );
}
